import {
  APP_INITIALIZED,
  INIT_APP,
  UPDATE_TRADING_VALUES,
  REFRESH_TRADING_VALUES,
  CLEAR_STATE,
  CHART_READY,
  APPLY_POST_COMMAND,
  TRIGGER_POST_COMMAND,
  SET_CHART_MODE,
  SET_APP_MODE,
  SET_APP_THEME,
  SET_POSITIONS_MODE,
  SET_LOCALE,
  SET_TRADING_SYMBOL,
  SET_TRADING_SIGNAL,
  CLEAR_TRADING_SIGNAL,
  DISMISS_RISC_DISCLAIMER,
  REFRESH_CHART,
} from '../constants'
import { Action } from 'redux'
import { AppMode, AppTheme, ChartMode, PositionsMode, TradingPaneValues } from '../types'
import { DISABLE_TRADE_CONTROLS, ENABLE_TRADE_CONTROLS } from '../constants/modal-const'
import { TradeCommandType } from '@bdswiss/mt4-connector'

export interface InitApp {
  type: INIT_APP
  mt4Version: number
  mt4Platform: string
  graphQlURL: string
  preferredAccountType: string
  accountLogin?: string
  authToken?: string
  defaultSymbol: string
  mode: string
  demo?: string
  locale?: string
  dismissedRiscDisclaimer: boolean
}

export interface AppInitialized {
  type: APP_INITIALIZED
  timeElapsed: number
}

export function initApp(
  mt4Version: number,
  mt4Platform: string,
  graphQlURL: string,
  mode: string,
  defaultSymbol: string,
  preferredAccountType: string,
  dismissedRiscDisclaimer: boolean,
  locale?: string,
  accountLogin?: string,
  demo?: string,
  authToken?: string
): InitApp {
  return {
    type: INIT_APP,
    mt4Version,
    mt4Platform,
    graphQlURL,
    preferredAccountType,
    accountLogin,
    authToken,
    mode,
    defaultSymbol,
    demo,
    locale,
    dismissedRiscDisclaimer,
  }
}

export interface InitAccounts {
  authToken: string
  url: string // graphQlURL
}

export interface RefreshTradingValues {
  type: REFRESH_TRADING_VALUES
  lotsInitial: number
  lots: number
  lotsInput: number
  amountInitial: number
  amount: number
  amountInput: number
  buy: TradingPaneValues
  sell: TradingPaneValues
}

export function refreshTradingValues(
  lotsInitial: number,
  lots: number,
  lotsInput: number,
  amountInitial: number,
  amount: number,
  amountInput: number,
  buy: TradingPaneValues,
  sell: TradingPaneValues
): RefreshTradingValues {
  return {
    type: REFRESH_TRADING_VALUES,
    lotsInitial,
    lots,
    lotsInput,
    amountInitial,
    amount,
    amountInput,
    buy,
    sell,
  }
}

export interface TradeValuesUpdate {
  value: number
  valueAccount: number
  requiredMargin: number
  requiredMarginAccount: number
  takeProfitInitial: number
  profit: number
  profitPercent: number
  stopLossInitial: number
  loss: number
  lossPercent: number
  priceInitial: number
  price: number
}

export interface UpdateTradingValues extends Action {
  type: UPDATE_TRADING_VALUES

  lotsInitial: number
  lots: number
  amountInitial: number
  buy: TradeValuesUpdate | TradingPaneValues
  sell: TradeValuesUpdate | TradingPaneValues
}

export function updateTradingValues(
  lotsInitial: number,
  lots: number,
  amountInitial: number,
  buy: TradeValuesUpdate | TradingPaneValues,
  sell: TradeValuesUpdate | TradingPaneValues
): UpdateTradingValues {
  return {
    type: UPDATE_TRADING_VALUES,
    lotsInitial,
    lots,
    amountInitial,
    buy,
    sell,
  }
}

export interface DisableTradeControls extends Action {
  type: DISABLE_TRADE_CONTROLS
  id?: string
}

export function disableTradeControls(id?: string): DisableTradeControls {
  return {
    type: DISABLE_TRADE_CONTROLS,
    id,
  }
}

export interface EnableTradeControls extends Action {
  type: ENABLE_TRADE_CONTROLS
  id?: string
}

export function enableTradeControls(id?: string): EnableTradeControls {
  return {
    type: ENABLE_TRADE_CONTROLS,
    id,
  }
}

export interface ClearState extends Action {
  type: CLEAR_STATE
}

export function clearState(): ClearState {
  return {
    type: CLEAR_STATE,
  }
}

export interface ChartReady extends Action {
  type: CHART_READY
}

export function chartReady(): ChartReady {
  return {
    type: CHART_READY,
  }
}

export interface SetAppMode extends Action {
  type: SET_APP_MODE
  mode: AppMode
}

export function setAppMode(mode: AppMode): SetAppMode {
  return {
    type: SET_APP_MODE,
    mode,
  }
}
export interface SetAppTheme extends Action {
  type: SET_APP_THEME
  theme: AppTheme
}

export function setAppTheme(theme: AppTheme): SetAppTheme {
  return {
    type: SET_APP_THEME,
    theme,
  }
}
export interface SetChartMode extends Action {
  type: SET_CHART_MODE
  mode: ChartMode
}

export function setChartMode(mode: ChartMode): SetChartMode {
  return {
    type: SET_CHART_MODE,
    mode,
  }
}

export interface RefreshChart extends Action {
  type: REFRESH_CHART
  refresh: boolean
}

export function refreshChart(refresh: boolean): RefreshChart {
  return {
    type: REFRESH_CHART,
    refresh
  }
}

export interface SetPositionsMode extends Action {
  type: SET_POSITIONS_MODE
  mode: PositionsMode
}

export function setPositionsMode(mode: PositionsMode): SetPositionsMode {
  return {
    type: SET_POSITIONS_MODE,
    mode,
  }
}

export interface TriggerPostCommand {
  type: TRIGGER_POST_COMMAND
  symbol: string
  tradeType: TradeCommandType
  timeScale?: number
  takeProfit?: number
  stopLoss?: number
}

export function triggerPostCommand(
  symbol: string,
  tradeType: TradeCommandType,
  timeScale?: number,
  takeProfit?: number,
  stopLoss?: number
): TriggerPostCommand {
  return {
    type: TRIGGER_POST_COMMAND,
    symbol,
    tradeType,
    timeScale,
    takeProfit,
    stopLoss,
  }
}

export interface ApplyPostCommand {
  type: APPLY_POST_COMMAND
  symbol: string
  tradeType: TradeCommandType
  range?: { from: number; to: number; timeScale: number; interval: string }
  takeProfit?: number
  stopLoss?: number
}

function timescaleToInterval(timeScale: number): string {
  if (timeScale <= 3) {
    return '1S'
  }
  if (timeScale <= 180) {
    return '1'
  }
  if (timeScale <= 4000) {
    return '30'
  }
  if (timeScale <= 11000) {
    return '60'
  }
  return '1D'
}

export function applyPostCommand(
  symbol: string,
  tradeType: TradeCommandType,
  timeScale?: number,
  takeProfit?: number,
  stopLoss?: number
): ApplyPostCommand {
  if (timeScale) {
    const to = Math.round(new Date().getTime() / 1000)
    const from = to - timeScale * 60
    const interval = timescaleToInterval(timeScale)
    const range = { from, to, timeScale, interval }
    return {
      type: APPLY_POST_COMMAND,
      symbol,
      tradeType,
      range,
      takeProfit,
      stopLoss,
    }
  }
  return {
    type: APPLY_POST_COMMAND,
    symbol,
    tradeType,
    takeProfit,
    stopLoss,
  }
}

export interface SetTradingSymbol {
  type: SET_TRADING_SYMBOL
  symbol: string
  tradeType: TradeCommandType
}

export function setTradingSymbol(symbol: string, tradeType: TradeCommandType): SetTradingSymbol {
  return {
    type: SET_TRADING_SYMBOL,
    symbol,
    tradeType,
  }
}

export interface SetTradingSignal {
  type: SET_TRADING_SIGNAL
  signal: any
}

export function setTradingSignal(signal?: any): SetTradingSignal {
  return {
    type: SET_TRADING_SIGNAL,
    signal,
  }
}

export interface ClearTradingSignal {
  type: CLEAR_TRADING_SIGNAL
}

export function clearTradingSignal(): ClearTradingSignal {
  return {
    type: CLEAR_TRADING_SIGNAL,
  }
}

export interface SetLocale {
  type: SET_LOCALE
  locale: string
}

export function setLocale(locale: string): SetLocale {
  return {
    type: SET_LOCALE,
    locale,
  }
}

export interface DismissRiscDisclaimer extends Action {
  type: DISMISS_RISC_DISCLAIMER
}

export function dismissRiscDisclaimer() {
  return {
    type: DISMISS_RISC_DISCLAIMER,
  }
}
